import { buildBlogPath, buildShowPath } from "../lib/routes.js";
import { getBlogPostBySlug } from "../lib/blogPosts.js";
import { DEFAULT_AMAZON_LINK, getShowAmazonLink } from "../lib/amazonLink.js";

const THEME_KEY = "howoldhere:theme";

// SVG icons
const ICON_MOON = `<svg class="icon-moon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
</svg>`;

const ICON_SUN = `<svg class="icon-sun" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <circle cx="12" cy="12" r="5"/>
  <line x1="12" y1="1" x2="12" y2="3"/>
  <line x1="12" y1="21" x2="12" y2="23"/>
  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
  <line x1="1" y1="12" x2="3" y2="12"/>
  <line x1="21" y1="12" x2="23" y2="12"/>
  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
</svg>`;

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function getCrumbs(route, store) {
  if (route.name === "home") {
    return '<span class="header-current">Shows</span>';
  }

  if (route.name === "blog") {
    return [
      '<a data-link href="/">Shows</a>',
      '<span class="header-separator">/</span>',
      '<span class="header-current">Blog</span>',
    ].join("");
  }

  if (route.name === "blog-post") {
    const post = getBlogPostBySlug(route.params.slug);
    const title = post ? escapeHtml(post.title) : "Missing post";

    return [
      '<a data-link href="/">Shows</a>',
      '<span class="header-separator">/</span>',
      '<a data-link href="/blog">Blog</a>',
      '<span class="header-separator">/</span>',
      `<span class="header-current">${title}</span>`,
    ].join("");
  }

  if (route.name === "credits") {
    return [
      '<a data-link href="/">Shows</a>',
      '<span class="header-separator">/</span>',
      '<span class="header-current">Credits</span>',
    ].join("");
  }

  if (route.name === "season") {
    const show = store.getShowById(route.params.id);
    return [
      '<a data-link href="/">Shows</a>',
      '<span class="header-separator">/</span>',
      `<a data-link href="${buildShowPath(route.params.id)}">${escapeHtml(show?.title ?? "Missing show")}</a>`,
      '<span class="header-separator">/</span>',
      `<span class="header-current">Season ${route.params.season}</span>`,
    ].join("");
  }

  if (route.name === "show") {
    const show = store.getShowById(route.params.id);
    const name = show ? show.title : "Missing show";
    return [
      '<a data-link href="/">Shows</a>',
      '<span class="header-separator">/</span>',
      `<span class="header-current">${name}</span>`,
    ].join("");
  }

  return [
    '<a data-link href="/">Shows</a>',
    '<span class="header-separator">/</span>',
    '<span class="header-current">Not found</span>',
  ].join("");
}

export function initTheme() {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === "dark" || stored === "light") {
      document.documentElement.dataset.theme = stored;
    }
  } catch {
    // ignore
  }
}

export function Header({ route, store }) {
  const header = document.createElement("header");
  const isHomeRoute = route.name === "home";
  const isShowRoute = route.name === "show" || route.name === "season";
  const isBlogIndexRoute = route.name === "blog";
  const isBlogPostRoute = route.name === "blog-post";
  const isBlogRoute = isBlogIndexRoute || isBlogPostRoute;
  const routeShow = isShowRoute ? store.getShowById(route.params.id) : null;
  const blogPost = isBlogPostRoute ? getBlogPostBySlug(route.params.slug) : null;
  const blogPostShow =
    isBlogPostRoute && blogPost?.showId ? store.getShowById(blogPost.showId) : null;
  const showAmazonHref = getShowAmazonLink(routeShow ?? blogPostShow);
  const shouldUseDefaultAmazonLink = isHomeRoute || isBlogIndexRoute;
  const headerAmazonHref = shouldUseDefaultAmazonLink
    ? DEFAULT_AMAZON_LINK
    : showAmazonHref;
  const shouldRenderHeaderCta = shouldUseDefaultAmazonLink || Boolean(showAmazonHref);
  const ctaStateClass = isShowRoute ? "header-amazon-cta--hidden" : "";
  header.className = `site-header ${isHomeRoute ? "is-home" : "is-show-or-other"}${isBlogRoute ? " is-blog" : ""}${isBlogIndexRoute ? " is-blog-index" : ""}${isBlogPostRoute ? " is-blog-post" : ""}`;

  header.innerHTML = `
    <a class="brand-link" data-link href="/">
      <span class="brand-mark" aria-hidden="true">
        <span class="brand-mark-grid">
          <span class="brand-mark-tile brand-mark-tile--sky"></span>
          <span class="brand-mark-tile brand-mark-tile--sunset"></span>
          <span class="brand-mark-tile brand-mark-tile--sand"></span>
          <span class="brand-mark-tile brand-mark-tile--palm"></span>
        </span>
      </span>
      <span class="brand-wordmark">HowOldHere</span>
    </a>
    <nav class="header-crumbs" aria-label="Breadcrumb">
      ${getCrumbs(route, store)}
    </nav>
    ${
      isBlogRoute
        ? ""
        : `<nav class="header-aux-links" aria-label="Secondary">
      <a data-link href="${buildBlogPath()}">Blog</a>
    </nav>`
    }
    ${
      shouldRenderHeaderCta
        ? `<a
      class="watch-cta header-amazon-cta ${ctaStateClass}"
      href="${headerAmazonHref}"
      target="_blank"
      rel="noopener noreferrer sponsored"
      aria-label="Watch on Amazon"
    >
      Watch on Amazon
    </a>`
        : ""
    }
    <button
      class="theme-toggle"
      type="button"
      aria-label="Toggle dark mode"
    >
      ${ICON_MOON}
      ${ICON_SUN}
    </button>
  `;

  const toggleBtn = header.querySelector(".theme-toggle");
  toggleBtn.addEventListener("click", () => {
    const html = document.documentElement;
    const isDark =
      html.dataset.theme === "dark" ||
      (!html.dataset.theme &&
        window.matchMedia("(prefers-color-scheme: dark)").matches);

    const next = isDark ? "light" : "dark";
    html.dataset.theme = next;

    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      // ignore
    }
  });

  return header;
}
